import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Input,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import AddRole from "./AddRole";
import { useDispatch, useSelector } from "react-redux";
// Icons
import CircleIcon from "@mui/icons-material/Circle";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PublishIcon from "@mui/icons-material/Publish";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { getRole } from "../../../../reduxToolkit/features/adminPanel/role";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import {
  createAdmin,
  getAdmins,
  uploadAdminPic,
} from "../../../../reduxToolkit/features/adminPanel/admin";
import { getNotification } from "../../../../reduxToolkit/features/adminPanel/notification";

export default function AddAdmin({
  addAdminDlg,
  setAddAdminDlg,
  translate5,
  translate6,
}) {
  // useStates
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [creatingRole, SetCreatingRole] = useState(false);
  const [usernameExist, setUsernameExist] = useState("");
  const [emailExist, setEmailExist] = useState("");

  const [roleList, setRoleList] = useState([]);
  // Form fields
  const [adminPic, setAdminPic] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [role, setRole] = useState("");
  // Add Role Section
  const [addRoleSection, setAddRoleSection] = useState(false);

  // Store
  const { roles } = useSelector((state) => state.role);
  // UseEffect
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getRole());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (roles.length > 0) {
      setRoleList(roles);
      setRole(roles[0]._id);
    }
  }, [roles]);

  useEffect(() => {
    addRoleSection ? SetCreatingRole(true) : SetCreatingRole(false);
  }, [addRoleSection]);

  useEffect(() => {
    if (addAdminDlg) {
      setSubmitError("");
      setUsernameExist(false);
      setEmailExist(false);
    }
  }, [addAdminDlg]);

  const handleAddAdmin = async () => {
    if (loading) return;
    setSubmitError("");
    setUsernameExist(false);
    setEmailExist(false);
    if (!username.trim() || !/^[^\s@]+@[^\s@]+$/.test(email.trim()) ||
        password.length < 8 || new TextEncoder().encode(password).length > 72) {
      setSubmitError("Use a name, email, and a password of at least 8 characters and at most 72 UTF-8 bytes.");
      return;
    }
    if (!roles.some(item => item._id === role)) {
      setSubmitError("Select a valid role.");
      return;
    }
    setLoading(true);
    try {
      const createdAdmin = { adminName: username.trim(), email: email.trim(), password, roleId: role };
      if (adminPic) {
        const data = new FormData();
        const filename = Date.now() + "-" + username.replace(/\s+/g, "") +
          "-admin-pic." + adminPic.name.split(".").slice(-1);
        data.append("name", filename);
        data.append("adminPic", adminPic);
        await dispatch(uploadAdminPic(data)).unwrap();
        createdAdmin.adminPic = filename;
      }
      const response = await dispatch(createAdmin(createdAdmin)).unwrap();
      if (response.emailFailed || response.usernameFailed || response.failed || response.severity !== "success") {
        setEmailExist(Boolean(response.emailFailed));
        setUsernameExist(Boolean(response.usernameFailed));
        setSubmitError(response.message || "Unable to create user. Please try again.");
        return;
      }
      dispatch(getNotification());
      dispatch(getAdmins());
      setAddAdminDlg(false);
      setUsername("");
      setEmail("");
      setPassword("");
      setAdminPic("");
    } catch (error) {
      setSubmitError(error?.message || "Unable to create user. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUserNameChange = (e) => {
    setUsername(e.target.value);
    setUsernameExist(false);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailExist(false);
    setInvalidEmail(false);
  };
  return (
    <Dialog
      open={addAdminDlg}
      fullWidth
      maxWidth="md"
      onClose={() => {
        if (loading) return;
        setAddAdminDlg(false);
        setUsername("");
        setEmail("");
        setPassword("");
        setAdminPic("");
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {translate5("Add Admin")}
      </DialogTitle>
      <DialogContent>
        {submitError && <Alert severity="error" role="alert">{submitError}</Alert>}
        <Grid container spacing={0} flexDirection="column">
          <Grid item xs={12}>
            <Grid
              container
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Grid item>
                <LazyLoadImage
                  effect="blur"
                  src={
                    adminPic !== ""
                      ? URL.createObjectURL(adminPic)
                      : "https://cdn2.iconfinder.com/data/icons/picons-basic-1/57/basic1-112_user_add_new-512.png"
                  }
                  alt="Halloween party"
                  style={{
                    objectFit: "cover",
                    width: "200px",
                  }}
                />
                <Typography variant="body2" align="center" color="secondary">
                  {translate5("Recommended")}: 🖼️(800x600)
                </Typography>
              </Grid>
              <Grid item position="relative">
                <label htmlFor="icon-button-file">
                  <Input
                    accept="image/*"
                    id="icon-button-file"
                    type="file"
                    sx={{ display: "none" }}
                    onChange={(e) => setAdminPic(e.target.files[0])}
                  />
                  <IconButton
                    color="primary"
                    aria-label="upload-picture"
                    component="span"
                  >
                    <PhotoCamera />
                  </IconButton>
                </label>
              </Grid>
              <Grid item>
                {adminPic && (
                  <Button
                    variant="contained"
                    size="small"
                    color="error"
                    onClick={() => setAdminPic("")}
                  >
                    {translate6("Remove Image")}
                  </Button>
                )}
              </Grid>

            </Grid>
          </Grid>
          <Grid item lg={12}>
            <TextField
              margin="dense"
              fullWidth
              size="small"
              label={translate5("User Name")}
              variant="standard"
              type="text"
              required
              value={username}
              onChange={handleUserNameChange}
              error={usernameExist ? true : false}
              helperText={
                usernameExist &&
                translate6("Username") + " " + translate6("Already Exist")
              }
            />

          </Grid>
          <Grid item lg={12}>
            <TextField
              margin="normal"
              fullWidth
              size="small"
              label={translate5("Email")}
              variant="standard"
              type="text"
              required
              value={email}
              onChange={handleEmailChange}
              onBlur={() => {
                email.length > 0 && !email.includes("@")
                  ? setInvalidEmail(true)
                  : setInvalidEmail(false);
              }}
              error={emailExist ? true : false}
              helperText={
                email.length > 0 &&
                (emailExist ? (
                  <Typography component="span" variant="body2" color="error">
                    {translate6("Email") + " " + translate6("Already Exist")}
                  </Typography>
                ) : (
                  invalidEmail && (
                    <Typography component="span" variant="body2" color="error">
                      {translate6("Invalid") + " " + translate6("Email")}
                    </Typography>
                  )
                ))
              }
            />

          </Grid>
          <Grid item lg={12}>
            <TextField
              margin="dense"
              fullWidth
              size="small"
              label={translate5("Password")}
              variant="standard"
              type={showPass ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size="small"
                      onClick={() => setShowPass(!showPass)}
                      disabled={password.length > 0 ? false : true}
                    >
                      {showPass ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />


          </Grid>

          <Grid item lg={12} sx={{ my: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="demo-simple-select-label">
                {translate5("Role")}
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={role}
                label={translate5("Role")}
                required
                onChange={(e) => setRole(e.target.value)}
              >
                {roleList.map((x, i) => (
                  <MenuItem key={i} value={x._id}>
                    <IconButton>
                      <CircleIcon sx={{ color: x.color }} />
                    </IconButton>
                    {x.roleName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item lg={12}>
            <Button
              variant="contained"
              size="small"
              color={addRoleSection ? "primary" : "warning"}
              fullWidth
              endIcon={
                addRoleSection ? (
                  <RemoveIcon sx={{ mb: 0 }} />
                ) : (
                  <AddIcon sx={{ mb: 0.5 }} />
                )
              }
              onClick={() => setAddRoleSection(!addRoleSection)}
            >
              {addRoleSection
                ? translate6("Minimize add role section")
                : translate5("Add Role")}
            </Button>
          </Grid>
          <Grid item>
            {addRoleSection && (
              <AddRole
                setAddRoleSection={setAddRoleSection}
                translate6={translate6}
              />
            )}
          </Grid>
        </Grid>
        {emailExist ? (
          <Alert severity="warning">
            {translate6("Email")} + " " + {translate6("Already Exist")}
          </Alert>
        ) : (
          usernameExist && (
            <Alert severity="warning">
              {translate6("Username")} + " " + {translate6("Already Exist")}
            </Alert>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setAddAdminDlg(false);
            setUsername("");
            setEmail("");
            setPassword("");
            setAdminPic("");
          }}
          autoFocus
          disabled={loading}
          color="error"
        >
          {translate5("Close")}
        </Button>
        <LoadingButton
          onClick={handleAddAdmin}
          endIcon={<PublishIcon />}
          loading={loading}
          loadingPosition="end"
          disabled={loading || creatingRole}
          variant="contained"
          size="small"
        >
          {translate5("Create")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
